import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE_PATH } from "@/lib/site-config";

export { SITE_URL, SITE_NAME };
export const DEFAULT_OG_IMAGE = `${SITE_URL}${DEFAULT_OG_IMAGE_PATH}`;

export interface PageMeta {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  locale?: string;
}

type TagSpec =
  | { kind: "name"; key: string; value: string }
  | { kind: "property"; key: string; value: string }
  | { kind: "link"; rel: string; value: string };

function applyTitle(value: string): () => void {
  const previous = document.title;
  document.title = value;
  return () => {
    document.title = previous;
  };
}

function applyMeta(spec: TagSpec): () => void {
  if (spec.kind === "link") {
    let tag = document.querySelector<HTMLLinkElement>(`link[rel="${spec.rel}"]`);
    const created = !tag;
    const previous = tag?.getAttribute("href") ?? null;
    if (!tag) {
      tag = document.createElement("link");
      tag.setAttribute("rel", spec.rel);
      document.head.appendChild(tag);
    }
    tag.setAttribute("href", spec.value);
    return () => {
      if (created) tag?.parentNode?.removeChild(tag);
      else if (previous !== null) tag?.setAttribute("href", previous);
    };
  }

  const attr = spec.kind === "name" ? "name" : "property";
  const selector = `meta[${attr}="${spec.key}"]`;
  let tag = document.querySelector<HTMLMetaElement>(selector);
  const created = !tag;
  const previous = tag?.getAttribute("content") ?? null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, spec.key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", spec.value);
  return () => {
    if (created) tag?.parentNode?.removeChild(tag);
    else if (previous !== null) tag?.setAttribute("content", previous);
  };
}

export function usePageMeta(meta: PageMeta) {
  const {
    title,
    description,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    type = "website",
    locale = "en_US",
  } = meta;

  useEffect(() => {
    const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

    const cleanups: Array<() => void> = [
      applyTitle(title),
      applyMeta({ kind: "name", key: "description", value: description }),
      applyMeta({ kind: "link", rel: "canonical", value: url }),
      applyMeta({ kind: "property", key: "og:title", value: title }),
      applyMeta({ kind: "property", key: "og:description", value: description }),
      applyMeta({ kind: "property", key: "og:url", value: url }),
      applyMeta({ kind: "property", key: "og:type", value: type }),
      applyMeta({ kind: "property", key: "og:image", value: image }),
      applyMeta({ kind: "property", key: "og:site_name", value: SITE_NAME }),
      applyMeta({ kind: "property", key: "og:locale", value: locale }),
      applyMeta({ kind: "name", key: "twitter:card", value: "summary_large_image" }),
      applyMeta({ kind: "name", key: "twitter:title", value: title }),
      applyMeta({ kind: "name", key: "twitter:description", value: description }),
      applyMeta({ kind: "name", key: "twitter:image", value: image }),
    ];

    return () => {
      for (let i = cleanups.length - 1; i >= 0; i--) cleanups[i]();
    };
  }, [title, description, path, image, type, locale]);
}
